import React, {useContext} from 'react'
import {
    Button,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {CancelButton, PreviousButton} from '../StepperButtons'
import {AppContext} from '../../contexts/AppContextProvider'
import DatePicker from '@mui/lab/DatePicker'
import {ComplaintTypeState} from '../../contexts/types'
import {isValidFn, useForm} from '../../hooks/useForm'
import {endOfToday, isAfter, isBefore, isDate, isSameDay, isValid, startOfDay,} from 'date-fns'
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import {setEventOccurrence} from "../../contexts/ComplaintTypeReducer";
import useTheHackiestPageNavigation from "../../hooks/useTheHackiestPageNavigation";

const isValidDate: isValidFn<ComplaintTypeState> = (value, data) => {
    if (!value) return true
    if (
        isDate(value) &&
        isValid(value) &&
        isBefore(value as Date, endOfToday()) &&
        (isSameDay(value as Date, startOfDay(new Date(1986, 0, 1))) ||
            isAfter(value as Date, startOfDay(new Date(1986, 0, 1))))
    ) {
        return true
    }
    return false
}

const EventOccurence: React.FC = () => {
    const {navigateForward} = useTheHackiestPageNavigation()
    const {t} = useContext(OrgCustomizationContext)
    const {dateMask, complaint, dispatch, postNotification, clearNotification} = useContext(AppContext)
    const required = {value: true, message: t('this_field_is_required')}
    const {
        data,
        errors,
        handleChangeEvent,
        handleChange,
        validateFields,
        enableOnChangeValidation,
    } = useForm<ComplaintTypeState>({
        initialValues: {
            ...complaint.complaintType,
        },
        validations: {
            location: {required},
            dateOfOccurrence: {
                required,
                custom: {
                    isValid: (value, data) => {
                        if (!value) return true
                        if (isValidDate(value, data)) {
                            return true
                        }
                        return false
                    },
                    message: t('please_enter_a_valid_date', {
                        earliestDate: t('earliest_date'),
                    }),
                },
            },
        },
    })

    const handleSubmit = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        const isFormValid = validateFields(data)

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        // Package up the form data and update app state
        dispatch(setEventOccurrence(data))

        navigateForward()
    }

    return (
        <Grid container>
            <Stack spacing={2}>
                <Typography variant='h5' sx={{mb: 1}}>
                    {t('eo_event_occurence')}
                </Typography>
                <FormControl sx={{maxWidth: '350px'}}>
                    <FormLabel sx={{mb: 1}}>{t('eo_select_date')}:</FormLabel>
                    <DatePicker
                        disableFuture
                        mask={dateMask}
                        value={data.dateOfOccurrence}
                        // onChange={handleChangeEvent('dateOfOccurrence')}
                        onChange={(date) => {
                            handleChange(
                                'dateOfOccurrence',
                                date as unknown as Date
                            )
                        }}
                        renderInput={(params) => <TextField {...params} />}
                    />
                    <FormHelperText error={!!errors.dateOfOccurrence}>
                        {errors.dateOfOccurrence}
                    </FormHelperText>

                    <Typography variant='body2' sx={{mt: 1}}>
                        {t('unknown_date_instructions')}
                    </Typography>
                </FormControl>
                <FormControl>
                    <FormLabel id='location-radio-buttons'>
                        {t('eo_select_location')}:
                    </FormLabel>
                    <RadioGroup
                        name='radio-buttons-group'
                        value={data.location}
                        onChange={handleChangeEvent('location')}
                    >
                        {complaint.generalComplaint.wasPatientInvolved ===
                            'NO' && (
                                <FormControlLabel
                                    value='1_LAB_INITIAL_INSPECTION'
                                    control={<Radio/>}
                                    label={t('eo_lab_initial_inspection') as string}
                                />
                            )}
                        {complaint.generalComplaint.wasPatientInvolved ===
                            'NO' && (
                                <FormControlLabel
                                    value='2_LAB_DURING_FINALIZATION'
                                    control={<Radio/>}
                                    label={
                                        t('eo_lab_during_finalization') as string
                                    }
                                />
                            )}
                        {complaint.generalComplaint.wasPatientInvolved ===
                            'NO' && (
                                <FormControlLabel
                                    value='3_CLINIC_INITIAL_INSPECTION'
                                    control={<Radio/>}
                                    label={
                                        t('eo_clinic_initial_inspection') as string
                                    }
                                />
                            )}
                        <FormControlLabel
                            value='4_CLINIC_DURING_PROCEDURE'
                            control={<Radio/>}
                            label={t('eo_clinic_during_procedure') as string}
                        />
                        {
                            complaint.generalComplaint.wasPatientInvolved === 'YES'
                            && <FormControlLabel
                                value='5_CLINIC_AFTER_PROCEDURE'
                                control={<Radio/>}
                                label={t('eo_clinic_after_procedure') as string}
                            />
                        }
                    </RadioGroup>
                    <FormHelperText error={!!errors.location}>
                        {errors.location}
                    </FormHelperText>
                </FormControl>
            </Stack>
            <Stack
                direction='row-reverse'
                spacing={2}
                sx={{pt: 4, width: '100%'}}
            >
                <Button
                    variant='contained'
                    color='primary'
                    onClick={handleSubmit}
                >
                    {t('continue')}
                </Button>

                <PreviousButton before={() => dispatch(setEventOccurrence(data))}/>

                <CancelButton/>
            </Stack>
        </Grid>
    )
}

export default EventOccurence
